/**
 * Signal Filters Component
 *
 * Filter controls for trading signals
 * Memoized for performance optimization
 */

import { memo } from 'react';
import { Icons } from '../Icons';
import type { SignalType } from '../../hooks/useTradingSignals';

interface SignalFiltersProps {
  filterAction: SignalType | 'all';
  filterConfidence: number;
  filterToken: string;
  filterTimeframe: string;
  onActionChange: (action: SignalType | 'all') => void;
  onConfidenceChange: (confidence: number) => void;
  onTokenChange: (token: string) => void;
  onTimeframeChange: (timeframe: string) => void;
  onReset: () => void;
}

/**
 * Signal Filters Component
 */
export const SignalFilters = memo(function SignalFilters({
  filterAction,
  filterConfidence,
  filterToken,
  filterTimeframe,
  onActionChange,
  onConfidenceChange,
  onTokenChange,
  onTimeframeChange,
  onReset
}: SignalFiltersProps) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '12px',
      marginBottom: '16px',
      padding: '16px',
      background: 'var(--bg)',
      borderRadius: '12px',
      border: '1px solid var(--border)'
    }}>
      {/* Action Filter */}
      <div>
        <label style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '6px', display: 'block', fontWeight: 600 }}>
          Action
        </label>
        <select
          aria-label="Action"
          value={filterAction}
          onChange={(e) => onActionChange(e.target.value as SignalType | 'all')}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1px solid var(--border)',
            background: 'var(--card)',
            color: 'var(--text)',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="all">All Actions</option>
          <option value="buy">Buy</option>
          <option value="sell">Sell</option>
          <option value="hold">Hold</option>
        </select>
      </div>

      {/* Confidence Filter */}
      <div>
        <label style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '6px', display: 'block', fontWeight: 600 }}>
          Min. Confidence: {filterConfidence}%
        </label>
        <input
          type="range"
          min="0"
          max="100"
          step="5"
          value={filterConfidence}
          onChange={(e) => onConfidenceChange(Number(e.target.value))}
          style={{
            width: '100%',
            cursor: 'pointer'
          }}
        />
      </div>

      {/* Token Search Filter */}
      <div>
        <label style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '6px', display: 'block', fontWeight: 600 }}>
          Token
        </label>
        <input
          type="text"
          placeholder="Search token..."
          value={filterToken}
          onChange={(e) => onTokenChange(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1px solid var(--border)',
            background: 'var(--card)',
            color: 'var(--text)',
            fontSize: '14px'
          }}
        />
      </div>

      {/* Timeframe Filter */}
      <div>
        <label style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '6px', display: 'block', fontWeight: 600 }}>
          Timeframe
        </label>
        <select
          aria-label="Timeframe"
          value={filterTimeframe}
          onChange={(e) => onTimeframeChange(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1px solid var(--border)',
            background: 'var(--card)',
            color: 'var(--text)',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="all">All Timeframes</option>
          <option value="24-48h">24-48h</option>
          <option value="2-5 days">2-5 days</option>
          <option value="1-2 weeks">1-2 weeks</option>
        </select>
      </div>

      {/* Reset Button */}
      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
        <button
          onClick={onReset}
          className="btn-ghost"
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          <Icons.X size={14} />
          Reset
        </button>
      </div>
    </div>
  );
});

SignalFilters.displayName = 'SignalFilters';
